/**
 * 海螺图片生成节点 - 支持文生图和角色生图（Minimax）
 */
import React, { memo, useState, useEffect, useCallback } from 'react';
import { useReactFlow, useStore, Handle, Position } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import ApiService from '../../../api/ApiService';
import LocalDataService from '../../../api/LocalDataService';
import { toast } from 'react-toastify';
import './MinimaxImageNode.css';

const apiService = new ApiService();

export const MinimaxImageNode = memo(({ id, data }) => {
  const { updateNodeData, getNode, getEdges } = useReactFlow();
  const { addNodesWithEdges, duplicateNode, createNodeAndConnect } = useNodeOperations();
  const edges = useStore((state) => state.edges);
  const { readOnly } = useProject();
  
  // 基础设置
  const [mode, setMode] = useState(data.mode || 't2i'); // 't2i' 或 'i2i'
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || '1:1');
  const numImages = 1; // 固定生成数量为1张
  
  // 高级设置
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [seed, setSeed] = useState(data.seed || '');
  const [promptOptimizer, setPromptOptimizer] = useState(data.promptOptimizer !== undefined ? data.promptOptimizer : true);
  const [aigcWatermark, setAigcWatermark] = useState(data.aigcWatermark || false);
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  // 同步节点数据
  useEffect(() => {
    updateNodeData(id, {
      mode,
      aspectRatio,
      numImages,
      seed,
      promptOptimizer,
      aigcWatermark,
    });
  }, [id, mode, aspectRatio, numImages, seed, promptOptimizer, aigcWatermark, updateNodeData]);

  // 获取连接的提示词
  const getConnectedPrompt = useCallback(() => {
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'prompt'
    );
    
    if (connectedEdges.length === 0) return null;
    
    const promptTexts = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      if (sourceNode?.type === 'promptInput' && sourceNode.data?.prompt) {
        promptTexts.push(sourceNode.data.prompt);
      }
    });
    
    return promptTexts.length > 0 ? promptTexts.join('\n') : null;
  }, [edges, getNode, id]);

  // 获取连接的图片（角色生图模式）
  const getConnectedImages = useCallback(() => {
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'image'
    );
    
    const images = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      
      // 从图片参考节点获取
      if (sourceNode?.type === 'imageReference' && sourceNode.data?.imageUrl) {
        images.push(sourceNode.data.imageUrl);
      }
      // 从手绘节点获取（只使用已上传到云端的图片）
      else if (sourceNode?.type === 'drawing' && sourceNode.data?.imageUrl) {
        const url = sourceNode.data.imageUrl;
        // 只使用已上传到云端的图片，过滤掉本地 base64 预览
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          images.push(url);
        }
      }
      // 从任务节点获取完成的图片
      else if (sourceNode?.type === 'imageTask' && sourceNode.data?.status === 'completed') {
        const generatedImages = sourceNode.data.generatedImages || [];
        generatedImages.forEach(img => {
          const url = typeof img === 'string' ? img : img.url;
          if (url) images.push(url);
        });
      }
      // 从图片展示节点获取
      else if (sourceNode?.type === 'imageDisplay' && sourceNode.data?.imageUrl) {
        images.push(sourceNode.data.imageUrl);
      }
    });
    
    return images;
  }, [edges, getNode, id]);

  // 验证连接
  const isValidConnection = useCallback((connection) => {
    // 如果连接到文本输入handle，只允许文本输入节点
    if (connection.targetHandle === 'prompt') {
      const sourceNode = getNode(connection.source);
      return sourceNode?.type === 'promptInput';
    }
    
    // 如果连接到图片输入handle
    if (connection.targetHandle === 'image') {
      const sourceNode = getNode(connection.source);
      const validTypes = ['imageReference', 'drawing', 'imageTask', 'imageDisplay'];
      
      if (!validTypes.includes(sourceNode?.type)) {
        return false;
      }
      
      // 限制图片数量
      const currentEdges = getEdges();
      const imageEdgesCount = currentEdges.filter(
        edge => edge.target === id && edge.targetHandle === 'image'
      ).length;
      
      if (imageEdgesCount >= 1) {
        toast.warning('角色生图模式最多支持1张参考图');
        return false;
      }
      
      return true;
    }
    
    return true;
  }, [id, getNode, getEdges]);

  // 处理 Handle 标签点击 - 自动创建节点并连接
  const handleHandleLabelClick = useCallback((e, handleId, handleType) => {
    e.stopPropagation();  // 阻止事件冒泡
    
    // 只读模式下禁用
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    
    // 调用统一的创建方法（会自动从配置读取限制）
    createNodeAndConnect(id, 'minimaxImage', handleId, handleType);
  }, [id, readOnly, createNodeAndConnect]);

  // 生成图片
  const handleGenerate = async () => {
    try {
      setError(null);
      setIsGenerating(true);
      
      // 1. 获取提示词
      const prompt = getConnectedPrompt();
      if (!prompt) {
        throw new Error('请先连接文本输入节点');
      }
      
      // 2. 获取用户 token
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        throw new Error('请先登录');
      }
      
      // 3. 构建请求参数
      const inputParams = {
        prompt: prompt,
        aspect_ratio: aspectRatio,
        n: numImages,
      };
      
      // 添加高级设置
      if (seed) {
        inputParams.seed = parseInt(seed);
      }
      if (promptOptimizer) {
        inputParams.prompt_optimizer = true;
      }
      if (aigcWatermark) {
        inputParams.aigc_watermark = true;
      }
      
      // 角色生图模式：添加参考图
      let appName = 'fal-ai/minimax/image-01';
      if (mode === 'i2i') {
        const images = getConnectedImages();
        if (images.length === 0) {
          throw new Error('角色生图模式需要连接参考图片节点');
        }
        
        inputParams.image_url = images[0];

        appName = 'fal-ai/minimax/image-01/subject-reference';
      }
      
      // 4. 调用 API 创建任务
      const response = await apiService.request(
        `${apiService.apiBase}/fal/tasks/create`,
        'POST',
        {
          token: userData.token,
          user_type: 1,
          app_name: appName,
          input_params: inputParams,
        }
      );
      
      if (response.code === 200 && response.data) {
        const taskId = response.data.task_id;
        toast.success('海螺图片任务已创建！');
        
        // 5. 创建任务节点
        const currentNode = getNode(id);
        const taskNodeId = nanoid();
        const taskNode = {
          id: taskNodeId,
          type: 'imageTask',
          position: {
            x: currentNode.position.x + 450,
            y: currentNode.position.y,
          },
          data: {
            taskId: taskId,
            taskType: mode === 'i2i' ? 'minimax-i2i' : 'minimax-t2i',
            status: 'pending',
            inputParams: inputParams,
            generatedImages: [],
          },
        };
        
        // 6. 创建连接边
        const newEdge = {
          id: nanoid(),
          source: id,
          target: taskNodeId,
          type: 'default',
        };
        
        // 7. 添加节点和边
        await addNodesWithEdges([taskNode], [newEdge]);
        
        setIsGenerating(false);
      } else {
        throw new Error(response.message || '创建任务失败');
      }
      
    } catch (err) {
      console.error('海螺图片生成失败:', err);
      const errorMessage = err?.message || '生成失败，请重试';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsGenerating(false);
    }
  };

  const handleDuplicate = (e) => {
    e.stopPropagation();
    duplicateNode(id);
  };

  return (
    <div className="MinimaxImageNode-container">
      {/* 复制按钮 */}
      <button 
        className="MinimaxImageNode-duplicate-button"
        onClick={handleDuplicate}
        title="复制节点"
      >
        <i className="bi bi-copy"></i>
      </button>

      {/* 文本输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="prompt"
        className="MinimaxImageNode-handle MinimaxImageNode-handle-prompt"
        style={{ top: '30%' }}
        isValidConnection={isValidConnection}
      >
        <div 
          className="MinimaxImageNode-handle-label MinimaxImageNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'prompt', 'text')}
          title="点击自动创建文本输入节点"
        >
          文本
        </div>
      </Handle>

      {/* 图片输入 Handle（仅角色生图模式显示） */}
      {mode === 'i2i' && (
        <Handle
          type="target"
          position={Position.Left}
          id="image"
          className="MinimaxImageNode-handle MinimaxImageNode-handle-image"
          style={{ top: '70%' }}
          isValidConnection={isValidConnection}
        >
          <div 
            className="MinimaxImageNode-handle-label MinimaxImageNode-handle-label-clickable"
            onClick={(e) => handleHandleLabelClick(e, 'image', 'image')}
            title="点击自动创建图片参考节点（最多1张）"
          >
            角色图
          </div>
        </Handle>
      )}

      {/* 输出 Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="MinimaxImageNode-handle MinimaxImageNode-handle-output"
      />

      <div className="MinimaxImageNode-title">海螺画图 (Minimax)</div>

      <div className="MinimaxImageNode-wrapper">
        {/* 连接状态显示 */}
        <div className="MinimaxImageNode-status">
          <div className="MinimaxImageNode-status-item">
            <span className="MinimaxImageNode-status-label">文本输入：</span>
            <span className={`MinimaxImageNode-status-value ${getConnectedPrompt() ? 'MinimaxImageNode-status-connected' : 'MinimaxImageNode-status-disconnected'}`}>
              {getConnectedPrompt() ? '✓ 已连接' : '✗ 未连接'}
            </span>
          </div>
          {mode === 'i2i' && (
            <div className="MinimaxImageNode-status-item">
              <span className="MinimaxImageNode-status-label">参考图输入：</span>
              <span className={`MinimaxImageNode-status-value ${getConnectedImages().length > 0 ? 'MinimaxImageNode-status-connected' : 'MinimaxImageNode-status-disconnected'}`}>
                {getConnectedImages().length > 0 ? '✓ 已连接' : '✗ 未连接'}
              </span>
            </div>
          )}
        </div>

        {/* 模式切换 */}
        <div className="MinimaxImageNode-field">
          <label className="MinimaxImageNode-label">生成模式</label>
          <div className="MinimaxImageNode-button-group">
            <button
              onClick={() => setMode('t2i')}
              disabled={isGenerating || readOnly}
              className={`MinimaxImageNode-button ${mode === 't2i' ? 'MinimaxImageNode-button-active' : ''}`}
            >
              文生图
            </button>
            <button
              onClick={() => setMode('i2i')}
              disabled={isGenerating || readOnly}
              className={`MinimaxImageNode-button ${mode === 'i2i' ? 'MinimaxImageNode-button-active' : ''}`}
            >
              图生图
            </button>
          </div>
        </div>

        {/* 宽高比 */}
        <div className="MinimaxImageNode-field">
          <label className="MinimaxImageNode-label">宽高比</label>
          <select
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
            disabled={isGenerating || readOnly}
            className="MinimaxImageNode-select"
          >
            <option value="1:1">1:1 正方形 (1024x1024)</option>
            <option value="16:9">16:9 横向宽屏 (1280x720)</option>
            <option value="9:16">9:16 竖向宽屏 (720x1280)</option>
            <option value="4:3">4:3 横向标准 (1152x864)</option>
            <option value="3:4">3:4 竖向标准 (864x1152)</option>
            <option value="3:2">3:2 横向照片 (1248x832)</option>
            <option value="2:3">2:3 竖向照片 (832x1248)</option>
            <option value="21:9">21:9 超宽屏 (1344x576)</option>
          </select>
        </div>

        {/* 高级设置 */}
        <div className="MinimaxImageNode-advanced">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="MinimaxImageNode-advanced-toggle"
            disabled={readOnly}
          >
            {showAdvanced ? '▼' : '▶'} 高级设置
          </button>

          {showAdvanced && (
            <div className="MinimaxImageNode-advanced-content">
              <div className="MinimaxImageNode-field">
                <label className="MinimaxImageNode-label">随机种子</label>
                <input
                  type="number"
                  value={seed}
                  onChange={(e) => setSeed(e.target.value)}
                  placeholder="留空为随机"
                  disabled={isGenerating || readOnly}
                  className="MinimaxImageNode-input"
                />
              </div>

              <div className="MinimaxImageNode-field">
                <label className="MinimaxImageNode-checkbox-label">
                  <input
                    type="checkbox"
                    checked={promptOptimizer}
                    onChange={(e) => setPromptOptimizer(e.target.checked)}
                    disabled={isGenerating || readOnly}
                    className="MinimaxImageNode-checkbox"
                  />
                  Prompt 自动优化
                </label>
              </div>

              <div className="MinimaxImageNode-field">
                <label className="MinimaxImageNode-checkbox-label">
                  <input
                    type="checkbox"
                    checked={aigcWatermark}
                    onChange={(e) => setAigcWatermark(e.target.checked)}
                    disabled={isGenerating || readOnly}
                    className="MinimaxImageNode-checkbox"
                  />
                  添加 AIGC 水印
                </label>
              </div>
            </div>
          )}
        </div>

        {/* 提示信息 */}
        <div className="MinimaxImageNode-hint">
          💡 连接文本输入节点{mode === 'i2i' ? '和参考图片节点' : ''}，点击生成后会自动创建任务节点
        </div>

        {/* 生成按钮 */}
        <button
          onClick={handleGenerate}
          disabled={isGenerating || readOnly}
          className={`MinimaxImageNode-generate ${
            isGenerating || readOnly ? 'MinimaxImageNode-generate-disabled' : ''
          }`}
        >
          {isGenerating ? '创建中...' : '创建任务'}
        </button>

        {/* 错误提示 */}
        {error && (
          <div className="MinimaxImageNode-error">
            ❌ {error}
          </div>
        )}
      </div>
    </div>
  );
});

MinimaxImageNode.displayName = 'MinimaxImageNode';

